import { useState } from "react";
import { DyteClock, DyteRecordingIndicator } from "@dytesdk/react-ui-kit";
import { useDyteMeeting } from "@dytesdk/react-web-core";
import { useMeeting, useUpsertMeeting } from "@vincer/services";
import { Loader2, PhoneOff, Share } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";

interface VideoHeaderProps {
  sessionId: string;
}

export default function VideoHeader({ sessionId }: VideoHeaderProps) {
  const navigate = useNavigate();
  const { data: meetingData } = useMeeting(sessionId);
  const { meeting } = useDyteMeeting();
  const { mutate: upsertMeeting } = useUpsertMeeting();

  const [recordingState, setRecordingState] = useState<
    "RECORDING" | "PAUSED" | "STOPPING" | "IDLE" | "STARTING"
  >("IDLE");

  let interval = setInterval(() => {
    if (meeting?.recording.recordingState) {
      setRecordingState(meeting?.recording.recordingState);
    }
  }, 1000);

  setTimeout(() => {
    clearInterval(interval);
  }, 15000);

  return (
    <div className="flex items-center justify-between border-b border-gray-700 bg-gray-800 p-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <div className="size-3 animate-pulse rounded-full bg-red-500"></div>
          <span className="text-sm font-medium">Live Session</span>
        </div>
        <div className="text-sm text-gray-300">
          Session with {meetingData?.mentee?.vincer_base_user?.name || "..."} •{" "}
          <DyteClock meeting={meeting} className="text-sm text-white/70" />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="text-white hover:bg-gray-700"
        >
          <Share className="mr-2 size-4" />
          Share Screen
        </Button>
        {recordingState === "IDLE" ? (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-300">
            <Loader2 className="mr-2 size-4 animate-spin" />
            Starting recording...
          </div>
        ) : (
          <DyteRecordingIndicator meeting={meeting} />
        )}
        <Button
          onClick={async () => {
            await upsertMeeting({
              uuid: sessionId,
              status: "ended",
              active: false,
            });

            await meeting.leave().then(() => {
              navigate(`/mentor/post-session?sessionId=${sessionId}`);
            });
          }}
          className="bg-red-600 text-white hover:bg-red-700"
        >
          <PhoneOff className="mr-2 size-4" />
          End Session
        </Button>
      </div>
    </div>
  );
}
